// Cloudscape's built-in select/multiselect autofilter matches against
// ['value', 'label', 'description', 'labelTag'] (see node_modules/@cloudscape-design/
// components/internal/components/option/utils/filter-options.js `matchesSingleOption`).
// When `value` is a UUID (user/group pickers, tag selectors, etc.) typing a digit or
// short hex string matches the UUID contents and surfaces unrelated rows (RSP-5284).
// Pre-filter on visible fields only so invisible values can't leak into matches.

interface VisibleOption {
  value?: string;
  label?: string;
  description?: string;
  labelTag?: string;
  tags?: readonly string[];
  filteringTags?: readonly string[];
}

interface VisibleOptionGroup extends VisibleOption {
  options: readonly VisibleOption[];
}

type VisibleOptionOrGroup = VisibleOption | VisibleOptionGroup;

const matchesField = (value: string | undefined, needle: string) =>
  !!value && value.toLowerCase().includes(needle);

const matchesTagsField = (
  values: readonly string[] | undefined,
  needle: string
) => !!values?.some((v) => matchesField(v, needle));

const optionMatches = (option: VisibleOption, needle: string): boolean =>
  matchesField(option.label, needle) ||
  matchesField(option.description, needle) ||
  matchesField(option.labelTag, needle) ||
  matchesTagsField(option.tags, needle) ||
  matchesTagsField(option.filteringTags, needle);

const isGroup = (option: VisibleOptionOrGroup): option is VisibleOptionGroup =>
  'options' in option && Array.isArray((option as VisibleOptionGroup).options);

export const filterOptionsByVisibleText = <T extends VisibleOptionOrGroup>(
  options: readonly T[] | undefined,
  filteringText: string
): readonly T[] | undefined => {
  if (!options || !filteringText) {
    return options;
  }
  const needle = filteringText.toLowerCase();

  return options.reduce<T[]>((acc, option) => {
    if (isGroup(option)) {
      if (matchesField(option.label, needle)) {
        acc.push(option);

        return acc;
      }
      const childMatches = option.options.filter((child) =>
        optionMatches(child, needle)
      );
      if (childMatches.length > 0) {
        acc.push({ ...option, options: childMatches });
      }

      return acc;
    }
    if (optionMatches(option, needle)) {
      acc.push(option);
    }

    return acc;
  }, []);
};
